import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  InfiniteScroll,
  LoadingSpinner,
  Popover,
  PopoverContentNoPortal,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TruncatedText,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { AlertTriangle, Check, ChevronsUpDown } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useInfiniteSelectShapes } from '../hooks/useInfiniteSelectShapes';
import { ShapeSelectType } from '../utils';
import * as Sentry from '@sentry/react';

export interface ShapeSelectProps {
  id?: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  allowedGeometryTypes?: string[];
  className?: string;
}

export function ShapeSelect({
  id,
  value,
  onChange,
  allowedGeometryTypes,
  className,
}: ShapeSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { messages } = useLocalization();
  const { filteredFlattenedShapes, isLoading, flattenedShapes } =
    useInfiniteSelectShapes(search, allowedGeometryTypes);

  const shape = useMemo(() => {
    return flattenedShapes?.find((shape) => shape.id === value);
  }, [flattenedShapes, value]);

  // Track reported missing shape to prevent duplicate Sentry messages
  const reportedMissingShapeRef = useRef<string | null>(null);

  // Report missing shape to Sentry (only after data has loaded, once per value)
  useEffect(() => {
    if (isLoading || !flattenedShapes.length) return;
    if (!value || value === 'Shape drawn in MDM') return;

    if (!shape && reportedMissingShapeRef.current !== value) {
      reportedMissingShapeRef.current = value;
      Sentry.captureMessage('Shape not found in ShapeSelect', {
        level: 'warning',
        tags: {
          component: 'ShapeSelect',
          context: 'shape-not-found',
        },
        extra: {
          missingShapeId: value,
          availableShapesCount: flattenedShapes.length,
          allowedGeometryTypes,
        },
      });
    } else if (shape) {
      // Reset if shape is found (e.g., user selected a valid shape)
      reportedMissingShapeRef.current = null;
    }
  }, [value, shape, isLoading, flattenedShapes.length, allowedGeometryTypes]);

  // Calculate dynamic height based on content
  const calculatedHeight = useMemo(() => {
    const COMMAND_INPUT_HEIGHT = 40;
    const COMMAND_ITEM_HEIGHT = 32;
    const LOADING_SPINNER_HEIGHT = 68;
    const COMMAND_EMPTY_HEIGHT = 68;
    const PADDING_AND_BORDERS = 10;
    const MAX_HEIGHT = 300;

    let contentHeight = COMMAND_INPUT_HEIGHT + PADDING_AND_BORDERS;

    if (isLoading) {
      // Add loading spinner height
      contentHeight += LOADING_SPINNER_HEIGHT;
    } else if (!filteredFlattenedShapes.length) {
      // Add empty state height
      contentHeight += COMMAND_EMPTY_HEIGHT;
    } else {
      // Add height for displayed shapes
      contentHeight += filteredFlattenedShapes.length * COMMAND_ITEM_HEIGHT;
    }

    return Math.min(contentHeight, MAX_HEIGHT);
  }, [filteredFlattenedShapes.length, isLoading]);

  // Get the appropriate empty state message based on allowedGeometryTypes
  const getEmptyStateMessage = () => {
    if (
      !allowedGeometryTypes ||
      allowedGeometryTypes.length === 0 ||
      allowedGeometryTypes.length >= 2
    ) {
      return messages.shapeSelectNoResults;
    }

    const singleType = allowedGeometryTypes[0].toLowerCase();

    switch (singleType) {
      case 'polygon':
        return messages.shapeSelectNoResultsPolygon;
      case 'linestring':
        return messages.shapeSelectNoResultsLines;
      case 'point':
        return messages.shapeSelectNoResultsPoints;
      case 'circle':
        return messages.shapeSelectNoResultsCircles;
      default:
        return messages.shapeSelectNoResults;
    }
  };

  return (
    <TooltipProvider>
      <Popover
        open={open}
        onOpenChange={() => {
          setOpen(!open);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            onClick={() => setOpen(!open)}
            className={cn(
              'w-full min-w-0 justify-between pr-3 text-base shadow-sm font-normal md:text-sm',
              className,
            )}
          >
            <div className="flex items-center min-w-0 flex-1 mr-2 overflow-hidden">
              {shape && (
                <span className="truncate block text-left">
                  {shape?.name ? shape.name : ''}
                </span>
              )}{' '}
              {!shape && value === 'Shape drawn in MDM' && (
                <span className="truncate block text-left">
                  Shape drawn in MDM
                </span>
              )}
              {!shape && value && value !== 'Shape drawn in MDM' && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="truncate text-left text-destructive flex items-center gap-1 cursor-help">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      {messages.shapeNotFound || 'Shape not found'}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>ID: {value}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {!shape && !value && (
                <span className="truncate block">
                  {messages.shapeSelectText}
                </span>
              )}
            </div>
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContentNoPortal
          align="start"
          className="w-full p-0 overflow-hidden"
          style={{ height: `${calculatedHeight}px` }}
        >
          <Command shouldFilter={false} className="flex flex-col h-full">
            <CommandInput
              id="shape-select-search-input"
              placeholder={messages.shapeSelectSearchText}
              onValueChange={(value) => {
                setSearch(value);
              }}
              value={search}
            />
            {!isLoading && (
              <CommandEmpty>{getEmptyStateMessage()}</CommandEmpty>
            )}
            {isLoading && <LoadingSpinner size="small"></LoadingSpinner>}
            {!isLoading && flattenedShapes.length > 0 && (
              <InfiniteScroll
                items={filteredFlattenedShapes}
                limit={30}
                observeResize={true}
                renderItem={(shapeSelect: ShapeSelectType) => {
                  return (
                    <CommandItem
                      key={shapeSelect.id}
                      disabled={shapeSelect.type === 'FeatureCollection'}
                      value={shapeSelect.id}
                      onSelect={(currentValue) => {
                        onChange(currentValue === value ? '' : currentValue);
                        setOpen(false);
                      }}
                    >
                      <div className="flex justify-between w-full">
                        <div className="flex min-w-0 max-w-[85%]">
                          <span>
                            {new Array(shapeSelect.level + 1).join(
                              '\u00A0\u00A0\u00A0',
                            )}
                          </span>
                          <div className="px-2 flex-shrink-0">
                            <shapeSelect.icon />
                          </div>
                          <TruncatedText
                            text={shapeSelect?.name ? shapeSelect.name : ''}
                            maxWidth="300px"
                          />
                        </div>
                        <Check
                          className={cn(
                            'mr-1 size-4 flex-shrink-0',
                            value === shapeSelect.id
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                      </div>
                    </CommandItem>
                  );
                }}
                className="flex-1 min-h-0 p-0 border rounded-lg"
              ></InfiniteScroll>
            )}
          </Command>
        </PopoverContentNoPortal>
      </Popover>
    </TooltipProvider>
  );
}
